import * as Sentry from '@sentry/browser';

import MD5 from 'crypto-js/md5';
import Dexie, { type Table } from 'dexie';

import {
  type FetchedDataDB,
  type LyricsDB,
  type SwipeColorsDB,
  fetchedDataTable,
  lyricsTable,
  swipeColorsTable,
} from './interfaces';

const ressourcesDBVersion = 3;

export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = SECONDS_IN_MINUTE * 60;
export const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;
export const MS_IN_DAY = SECONDS_IN_DAY * 1000;

class LocalDB extends Dexie {
  lyricsTable!: Table<LyricsDB>;
  swipeColorsTable!: Table<SwipeColorsDB>;
  fetchedDataTable!: Table<FetchedDataDB>;

  constructor() {
    super('Ressources');
    this.version(ressourcesDBVersion).stores({
      lyricsTable,
      swipeColorsTable,
      fetchedDataTable,
    });
  }

  generateHash = (data: string): string => MD5(data).toString();

  async fetchOrGetData<T>(
    url: string,
    // biome-ignore lint/complexity/noBannedTypes: <explanation>
    fetchMethod: Function,
    requestData: object = {},
    expiration: number = MS_IN_DAY,
  ): Promise<T> {
    const hashKey = this.generateHash(url + JSON.stringify(requestData));
    const cachedData = await this.fetchedDataTable.get(hashKey);

    if (cachedData && Date.now() - cachedData.timestamp < expiration) {
      this.deleteExpiredData(expiration);
      return cachedData.data as T;
    }
    const data = await fetchMethod(url, requestData).catch((error: Error) => {
      Sentry.captureException(error);
      throw error;
    });

    try {
      await this.fetchedDataTable.put({
        id: hashKey,
        url,
        data,
        timestamp: Date.now(),
      });
    } catch (error) {
      Sentry.captureException(new Error(`Error saving in indexed DB ${error.message}, url ${url}`));
    }

    return data as T;
  }

  async deleteExpiredData(expiration: number = MS_IN_DAY) {
    await this.fetchedDataTable
      .where('timestamp')
      .below(Date.now() - expiration)
      .delete();
  }

  async clearFetchedData() {
    await this.fetchedDataTable.clear();
  }
}

const localDB = new LocalDB();

export default localDB;
